import useBackend, { BackendContextType } from '@contexts/useBackend/useBackend';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@components/common/Toast/Toast';
import { createCubeClient } from '@infrastructure/cubeClient';
import { resolveLayout } from './autoGrid';
import { PersistedWidgetLayout, ValidationError, ValidatorFn, WidgetLayout } from './types';
import classes from './WidgetLayout.module.css';
import WidgetLayoutEditor from './WidgetLayoutEditor/WidgetLayoutEditor';
import WidgetLayoutViewer from './WidgetLayoutViewer/WidgetLayoutViewer';
import { getDeviceWidgetTypes, Widget, WidgetType } from './Widgets/types';
import type { DateRangeItem, FilterItem, WidgetDefinition } from './Widgets/WidgetBuilder/types';
import { parseWidgetDefinition, widgetDefinitionToCubeQuery } from './Widgets/widgetDefinition';
import { toBuilderLayout, toDomainLayout } from './Widgets/widgetDefinitionAdapter';
import { validateDomainLayout } from './Widgets/widgetSchemaValidator';
import {
  DATALESS_WIDGET_DATA,
  asReadmodelQueryDispatchTable,
  extractWidgetData,
  fetchReadmodelWidgetData,
  isDatalessWidget,
  isReadmodelWidget,
  type WidgetDataState
} from './Widgets/widgetData';
import { useTranslation } from 'react-i18next';
import type { Query, ResultSet } from '@cubejs-client/core';

/**
 * 2026-09-02 — the outcome of PREPARING one widget, which is not the same thing as
 * fetching it any more.
 *
 * A readmodel / dataless / malformed widget resolves to a final `WidgetDataState`
 * in one step, exactly as before. A Cube widget cannot: it becomes a standing
 * SUBSCRIPTION that yields many results over time, so preparation can only hand
 * back the query and let the effect own the socket. Splitting the two is what lets
 * `prepareWidgetData` stay an `async` function while Cube data arrives by push.
 */
type WidgetPreparation =
  | { kind: 'settled'; state: WidgetDataState }
  | { kind: 'cube'; definition: WidgetDefinition; cubeQuery: Query };

/**
 * PREFACTOR-grammar — this component IS the persistence seam.
 *
 * IN: a `PersistedWidgetLayout` (the domain/wire grammar — a readmodel row, a
 * curated default, an LLM-generated preview). OUT (`onChange`): the same grammar,
 * ready to hand straight to `AddUiWidgetLayout` / `ChangeUiWidgetLayout`.
 *
 * INSIDE: the builder-form grammar, which is what the editor, the viewer, the
 * auto-grid, the validators and `widgetDefinitionToCubeQuery` all speak. The two
 * conversions happen here and NOWHERE else — see `widgetDefinitionAdapter` for why
 * crossing that line twice is a bug (it is not idempotent, and it throws if you
 * try).
 */
export type WidgetLayoutEditorProps = {
  layout: PersistedWidgetLayout;
  onChange: ( layout: PersistedWidgetLayout ) => void;
  availableWidgets: WidgetType[];
  isEditModeOn: boolean;
  validators?: ValidatorFn[]; //custom validators, if no custom, defaults are used
  onValidationError?: ( errors: ValidationError[]) => void; //if upstream error handling is needed
  scale?: number;
  /**
   * TV1-1936 (run decision d7) — RUNTIME query overrides, applied to every widget in
   * the layout, at fetch time only. **Never persisted.**
   *
   * A widget's `filters` and `dateRange` are baked into its stored definition, so the
   * engine had no seam for "show me this same report, but for last quarter" — which is
   * exactly what the Dashboard's period filter has always done for its (now retired)
   * hardcoded Cube queries. Deleting the toolbar would have been more literal to
   * ADR-050 and would have lost a real affordance; this is ~30 lines instead.
   *
   * `dateRange` REPLACES the widget's own window, keeping the widget's own time
   * DIMENSION (`Sales.orderDate` vs `ReturnRate.saleDate` vs `Conversion.visitDate`
   * are different members on different cubes — the caller cannot know which, and must
   * not have to). A widget with no time dimension at all (the seller-health snapshot,
   * the critical-stock list) is left ALONE: forcing a window onto a snapshot cube would
   * blank it, which is a silent wrong answer, not a filtered one.
   *
   * `filters` are APPENDED to the widget's own (Cube ANDs them), so an override can
   * narrow a curated report but never widen it past what its author intended.
   */
  filterOverrides?: WidgetFilterOverrides;
  /**
   * Whether the report renders on a SHEET or directly in the page.
   *
   * Forwarded to the viewer; see `WidgetLayoutViewer`'s prop for the reasoning.
   * The EDITOR always keeps its paper — you are authoring a document there, and the
   * sheet is what shows you its edges.
   */
  surface?: 'paper' | 'plain';
};

/**
 * The `parseWidgetDefinition` messages a card being CONFIGURED can produce, mapped
 * to translation keys (2026-08-17).
 *
 * Only these three are reachable from the builder — they are the three fields a
 * blank card starts without. The parser's other messages describe a MALFORMED
 * persisted definition (a bad filter shape, an unsupported type), which is a
 * developer-facing condition; those pass through in English rather than paying for
 * a translation nobody will read.
 *
 * Keyed by the parser's exact English string. That coupling is deliberate and
 * cheap: the fallback is the key itself, so if a message is ever reworded the tile
 * degrades to the English wording rather than breaking.
 */
const INCOMPLETE_REASON_KEYS: Record<string, { key: string; fallback: string }> = {
  'name is required': { key: 'widget_incomplete_name', fallback: 'un nombre' },
  'cube (data source) is required': { key: 'widget_incomplete_cube', fallback: 'una fuente de datos' },
  'measures must contain at least one measure': {
    key: 'widget_incomplete_measures',
    fallback: 'al menos una métrica'
  }
};

/** @see WidgetLayoutEditorProps.filterOverrides */
export type WidgetFilterOverrides = {
  dateRange?: DateRangeItem;
  filters?: FilterItem[];
};

/**
 * Apply the runtime overrides to ONE builder-form definition, non-destructively.
 *
 * Runs INSIDE `getWidgetData` — i.e. AFTER `toBuilderLayout` and BEFORE
 * `widgetDefinitionToCubeQuery`. That position is the whole design: it is the only
 * point where the definition is in the grammar the overrides speak, and it keeps the
 * override off the write path entirely, so `handleLayoutChange` can never persist a
 * transient toolbar selection into the report.
 */
export const applyFilterOverrides = (
  definition: WidgetDefinition,
  overrides?: WidgetFilterOverrides
): WidgetDefinition => {
  if ( !overrides || ( !overrides.dateRange && !overrides.filters?.length )) return definition;

  const overridden: WidgetDefinition = { ...definition };

  // Keep the widget's OWN time dimension member; take only the WINDOW from the
  // override. And leave a widget that has no time dimension untouched — see the prop
  // docblock.
  if ( overrides.dateRange && definition.dateRange ) {
    overridden.dateRange = {
      ...overrides.dateRange,
      field: definition.dateRange.field,
      // The widget's granularity is a presentation choice (a monthly line chart vs a
      // total); the toolbar is choosing a PERIOD, not a bucket size.
      granularity: definition.dateRange.granularity,
      compare: definition.dateRange.compare
    };
  }

  if ( overrides.filters?.length ) {
    overridden.filters = [ ...( definition.filters ?? []), ...overrides.filters ];
  }

  return overridden;
};

const WidgetLayoutComponent = ({ layout, onChange, availableWidgets, isEditModeOn = true, validators, onValidationError, scale = 1, filterOverrides, surface = 'paper' }:WidgetLayoutEditorProps ) => {
  //state
  const { t } = useTranslation( 'widgets' );
  const { backend } = useBackend() as BackendContextType;
  const cubeApi = useMemo(
    () => createCubeClient({ baseUrl: import.meta.env.VITE_BACKEND_URL || '' }),
    []
  );
  // Domain -> builder, once per incoming layout. Everything below this line works
  // in the builder grammar.
  const builderLayout = useMemo(() => toBuilderLayout( layout ) as WidgetLayout, [layout]);
  // TV1-1936 (d7) — a VALUE key for the runtime overrides, so the fetch effect below
  // keys on what they say rather than on the identity of the object that said it.
  const filterOverridesKey = useMemo(
    () => ( filterOverrides ? JSON.stringify( filterOverrides ) : '' ),
    [filterOverrides]
  );
  /**
   * TV1-2291 (§6) — a READINESS GATE, and it is emphatically NOT `backend` in the
   * dependency array. Getting this wrong doubles every report's Cube traffic.
   *
   * `useBackend` hands back `undefined` on the first render and the real client one
   * effect later (`useProvideBackend`: a `useState` filled by a mount effect). So a
   * report that mounts in the same commit as the provider — a hard load straight
   * onto a report URL — sees `backend` go `undefined -> client` **while it is
   * mounted**. Three consequences, in the order they bite:
   *
   *  1. `backend` in the deps => the fetch effect runs TWICE, and every widget
   *     issues two Cube round trips. `N widgets = N round trips` is already this
   *     engine's bottleneck (SDD); silently making it `2N` is the worst kind of
   *     regression, because nothing is visibly broken. `curatedKpiRow.test.tsx` is
   *     the only thing that notices — it asserts 7 loads for 7 tiles, and it caught
   *     exactly this (14).
   *  2. A raw `backend` dep is worse still with a context value that is not
   *     referentially stable: the effect ends by calling `setWidgetDataMap` /
   *     `setIsLoadingData`, so it re-renders the component, so an unstable dep
   *     re-fetches FOREVER.
   *  3. But dropping the dependency entirely is not right either — a readmodel
   *     widget that lost the race would settle into its error tile and never retry.
   *
   * The gate resolves all three. Readiness is a BOOLEAN (it cannot churn) and the
   * effect **returns early** while it is false, so the pre-client pass fetches
   * NOTHING and the post-client pass fetches everything exactly once. Total across
   * the transition: N, not 2N. When the client is already there — the normal case,
   * since the provider sits at the app root — readiness never changes and the effect
   * runs a single time.
   */
  const isBackendReady = Boolean( backend );
  const [ editMode, setEditMode ] = useState<boolean>();
  const [ layoutState, setLayoutState ] = useState<WidgetLayout>( builderLayout );
  const [ errors, setErrors ] = useState<ValidationError[]>([]);
  const [ widgetDataMap, setWidgetDataMap ] = useState<Record<string, WidgetDataState>>({});
  const [ isLoadingData, setIsLoadingData ] = useState<boolean>( false );
  const [ deviceAvailableWidgets, setDeviceAvailableWidgets ] = useState<WidgetType[]>( availableWidgets );
  //effect
  useEffect(() => { 
     setEditMode( isEditModeOn );
  },[isEditModeOn]);
  useEffect(() => {
      if ( errors.length ) {
        // Group errors by code to show only one notification per error code
        const uniqueErrors = errors.reduce(( acc, error ) => {
          const errorCode = error.code || error.name || 'unknown';
          if ( !acc[errorCode]) {
            acc[errorCode] = error;
          }
          return acc;
        }, {} as Record<string, ValidationError> );
        
        // Show one notification per unique error code
        Object.values( uniqueErrors ).forEach( error => {
          toast.error({
            title: error.name || 'Error',
            message: error.message,
            onClose: () => setErrors( prev => prev.filter( e => ( e.code || e.name ) !== ( error.code || error.name )))
          });
        });
      }
    }, [errors]);
  /**
   * PREFACTOR-grammar — returns a DISCRIMINATED OUTCOME, never `null`.
   *
   * It used to `catch` -> `console.error` -> `return null`, and the caller then
   * simply omitted the widget from the map — which `WebWidget` reads as "still
   * loading" and renders as a Skeleton. Forever. A broken Cube query (a measure
   * that does not exist, a 401, a malformed definition) was therefore
   * indistinguishable from a slow one, to the user AND to anyone debugging it.
   *
   * TV1-2291 (§6) — there are now THREE branches here and no more: a readmodel
   * query, the dataless bypass, and Cube. Every one of them returns the same
   * discriminated outcome, and nothing downstream (`WebWidget`'s parsers, its
   * props-builders) knows which one ran.
   */
  const prepareWidgetData = async ( widget: Widget ): Promise<WidgetPreparation> => {
    if ( !widget ) {
      return { kind: 'settled', state: { status: 'error', error: t( 'widget_error_unknown', 'This widget could not be loaded' ) }};
    }

    try {
      // TV1-2291 (§6) — THE READMODEL BRANCH, and it must come FIRST.
      //
      // A readmodel widget (`count` / `alert` / `list` / `progress`) has no `cube`
      // and no `measures`, so `parseWidgetDefinition` below rejects it outright
      // (`cube (data source) is required`). It also has no Cube client to wait for:
      // gating it behind `cubeApi` would make an operational tile unrenderable
      // because an ANALYTICS dependency was missing.
      //
      // `filterOverrides` are handed straight through — `fetchReadmodelWidgetData`
      // routes them through `readmodelSource.isReadmodelFilter` (K11), where a
      // Cube-shaped clause fails the guard and is DROPPED. It is never translated:
      // an unfiltered readmodel tile is visibly the whole dataset, whereas a
      // mistranslated one is a plausible number that answers no question.
      if ( isReadmodelWidget( widget.definition )) {
        // Unreachable through the fetch effect, which will not run at all until the
        // client has arrived (`isBackendReady`). Kept so this function is correct on
        // its own terms rather than on its caller's, and so the failure — if a future
        // caller does skip the gate — is a NAMED error tile rather than a `TypeError`
        // deep inside the dispatch.
        if ( !backend ) {
          return { kind: 'settled', state: { status: 'error', error: t( 'widget_error_no_data_client', 'No data client available' ) }};
        }

        return {
          kind: 'settled',
          state: {
            status: 'ok',
            data: await fetchReadmodelWidgetData(
              widget.definition,
              // The narrowing that makes a server-side operation rename a COMPILE
              // error here instead of a silently empty widget at runtime.
              asReadmodelQueryDispatchTable( backend.queries ),
              filterOverrides
            )
          }
        };
      }

      /*
       * TWO OUTCOMES, NOT ONE (2026-08-17). `parseWidgetDefinition` throws for
       * exactly one reason — the definition is not complete enough to build a query
       * from — so its failure is `incomplete`, never `error`. See
       * `isWidgetDataIncomplete` for why that distinction is the difference between
       * "configure me" and a red "Widget failed to load" on every card anyone adds.
       *
       * Deliberately its OWN try/catch, ahead of the fetch: put this inside the
       * outer one and a Cube 401 would be indistinguishable from a missing measure,
       * which is the collapse being undone here.
       */
      let definition: WidgetDefinition;
      try {
        definition = parseWidgetDefinition( widget.definition );
      } catch ( error ) {
        const message = error instanceof Error ? error.message : String( error );
        return {
          kind: 'settled',
          state: {
          status: 'incomplete',
          // The validator emits "Invalid widget definition: a; b; c" — strip its
          // prefix and split, so the tile lists the fields instead of one run-on
          // sentence, then translate the three that a card being configured
          // actually hits. `parseWidgetDefinition` is a pure parser with no `t`, and
          // threading i18n through it would put UI copy in a grammar module; the
          // boundary that has `t` is here. Anything outside the closed set falls
          // through unchanged (English, but only reachable from a malformed
          // persisted definition, never from the builder).
          reasons: message.replace( /^[^:]*:\s*/, '' ).split( ';' )
.map(( reason ) => {
            const key = INCOMPLETE_REASON_KEYS[reason.trim()];
            return key ? t( key.key, key.fallback ) : reason.trim();
          })
.filter( Boolean )
          }
        };
      }

      // TV1-1930 — a DATALESS widget (`text`) has no cube and no measures, so
      // there is no query to send. `widgetDefinitionToCubeQuery` would hand back
      // `{}` and a Cube round trip on `{}` would error.
      if ( isDatalessWidget( definition )) {
        return { kind: 'settled', state: { status: 'ok', data: DATALESS_WIDGET_DATA }};
      }

      if ( !cubeApi ) {
        return { kind: 'settled', state: { status: 'error', error: t( 'widget_error_no_client', 'No analytics client available' ) }};
      }

      // TV1-1936 (d7) — runtime overrides land HERE: after the definition is in the
      // builder grammar, before it becomes a Cube query, and entirely off the write
      // path. Validate first, then override, so a broken persisted definition still
      // reports its own error rather than one the toolbar accidentally introduced.
      const cubeQuery = widgetDefinitionToCubeQuery( applyFilterOverrides( definition, filterOverrides ));
      // 2026-09-02 — the query is RETURNED, not run. A Cube widget is now a standing
      // SUBSCRIPTION rather than a one-shot fetch, and a subscription cannot be
      // expressed as an awaited value: it yields many results over time. The effect
      // below owns the socket and the unsubscribe; this function's job ends at
      // "here is the query that answers this widget".
      return { kind: 'cube', definition, cubeQuery };
    } catch ( error ) {
      console.error( `Error preparing data for widget ${widget.definition?.id}:`, error );
      return {
        kind: 'settled',
        state: {
          status: 'error',
          error: error instanceof Error
            ? error.message
            : t( 'widget_error_unknown', 'This widget could not be loaded' )
        }
      };
    }
  };
  useEffect(() => {
    // TV1-2291 (§6) — THE READINESS GATE, and it is an early return rather than a
    // dependency for the reason documented on `isBackendReady`. Fetching nothing
    // while the client is absent is what keeps the undefined -> client transition
    // costing N round trips in total instead of 2N: the first pass does no work, the
    // second does all of it. Placed ABOVE the loading guard on purpose — flipping
    // `isLoadingData` for a pass that cannot fetch would suppress the real one.
    if ( !isBackendReady ) return;
    if ( !layoutState || !layoutState.widgets.length ) return;

    /*
     * 2026-09-02 — ONE SUBSCRIPTION PER WIDGET, and the map is now written
     * INCREMENTALLY rather than swapped wholesale at the end.
     *
     * That change is forced by the transport, not chosen: `subscribe` delivers many
     * results over time, at moments nothing here controls, so there is no "all
     * done" instant at which to publish a complete map. Each widget's message
     * updates its own key and re-renders its own tile — which is also the better
     * behaviour, since a slow cube no longer holds back the six tiles beside it.
     *
     * Entries are NOT cleared on setup. A period change re-runs this effect, and
     * blanking the map would flash every tile back to a Skeleton before the first
     * message lands; leaving the previous values in place lets each tile update
     * underneath itself. Stale ids from a removed widget are pruned instead.
     */
    let cancelled = false;
    const subscriptions: Array<{ unsubscribe: () => void }> = [];

    const publish = ( widgetId: string, state: WidgetDataState ) => {
      if ( cancelled ) return;
      setWidgetDataMap( prev => ({ ...prev, [widgetId]: state }));
    };

    const currentIds = new Set( layoutState.widgets.map( widget => widget.definition.id ));
    setWidgetDataMap( prev =>
      Object.fromEntries( Object.entries( prev ).filter(([id]) => currentIds.has( id ))));

    setIsLoadingData( true );

    const start = async () => {
      const preparations = await Promise.all(
        layoutState.widgets.map( async widget => ({
          widgetId: widget.definition.id,
          preparation: await prepareWidgetData( widget )
        }))
      );
      if ( cancelled ) return;

      for ( const { widgetId, preparation } of preparations ) {
        // Readmodel / dataless / failed-to-prepare widgets are already settled —
        // they never touch Cube, so there is nothing to subscribe to.
        if ( preparation.kind === 'settled' ) {
          publish( widgetId, preparation.state );
          continue;
        }

        const { definition, cubeQuery } = preparation;
        /*
         * `subscribe` re-invokes this callback on EVERY push for this query, so it
         * must stay a pure projection — no accumulation, no closure state that a
         * second call would corrupt.
         *
         * The `(error, resultSet)` shape is the callback's own contract: Cube hands
         * back one or the other, and an error must land in the tile rather than be
         * swallowed, or a failing subscription is indistinguishable from a slow one
         * (the defect the discriminated `WidgetDataState` exists to prevent).
         */
        const subscription = cubeApi.subscribe(
          cubeQuery,
          {},
          ( error: Error | null, resultSet: ResultSet | null ) => {
            if ( cancelled ) return;
            if ( error || !resultSet ) {
              console.error( `Error fetching data for widget ${widgetId}:`, error );
              publish( widgetId, {
                status: 'error',
                error: error?.message ?? t( 'widget_error_unknown', 'This widget could not be loaded' )
              });
              return;
            }
            try {
              publish( widgetId, {
                status: 'ok',
                // TV1-1929 — project the ResultSet per widget type instead of
                // flattening it to `rawData()` for everyone. New projections go in
                // `extractWidgetData`, NOT here.
                data: extractWidgetData( definition, cubeQuery, resultSet )
              });
            } catch ( projectionError ) {
              console.error( `Error projecting data for widget ${widgetId}:`, projectionError );
              publish( widgetId, {
                status: 'error',
                error: projectionError instanceof Error
                  ? projectionError.message
                  : t( 'widget_error_unknown', 'This widget could not be loaded' )
              });
            }
          }
        );

        /*
         * ⚠️ REGISTER EVEN IF WE ARE ALREADY CANCELLED. `subscribe` opened a server
         * -side subscription the moment it was called; returning early without
         * pushing it here would leak it for the life of the socket, and the server
         * would keep re-running that query every tick for a widget nobody is
         * watching.
         */
        subscriptions.push( subscription );
      }

      if ( !cancelled ) setIsLoadingData( false );
    };

    void start().catch(( error ) => {
      console.error( 'Error subscribing to widget data:', error );
      if ( !cancelled ) setIsLoadingData( false );
    });

    return () => {
      cancelled = true;
      subscriptions.forEach(( subscription ) => {
        try {
          subscription.unsubscribe();
        } catch ( error ) {
          console.error( 'Error unsubscribing from widget data:', error );
        }
      });
    };
    // TV1-1936 (d7) — `filterOverridesKey`, not `filterOverrides` itself. The prop is
    // an object literal at every call site, so a reference dep would refetch on every
    // parent render; a value dep refetches exactly when the selected period/channel
    // actually changes.
    //
    // TV1-2291 (§6) — `isBackendReady` joins them, and it is a BOOLEAN rather than
    // `backend` itself for the reason spelled out where it is computed: this effect
    // re-renders the component, so a dep with unstable identity re-fetches forever.
  }, [ layoutState, cubeApi, isBackendReady, filterOverridesKey ]);
  /**
   * ⚠️ WHILE EDITING, THE LOCAL WORKING COPY IS AUTHORITATIVE (2026-08-17).
   *
   * This was `useEffect(() => setLayoutState( builderLayout ), [builderLayout])` —
   * i.e. "any new `layout` prop replaces the editor's state" — and it silently
   * destroyed work in progress.
   *
   * `Reports.tsx` builds `selectedLayout` with
   * `useMemo(() => visibleLayouts.find(...), [visibleLayouts, selectedLayoutId])`
   * over `layoutList`, which is filled **straight off a readmodel subscription**.
   * So every subscription push — including the one our OWN save provokes — mints a
   * fresh object for the same report, `builderLayout` recomputes, and this effect
   * overwrote `layoutState` with the persisted version.
   *
   * The result was a builder that could not be used at all: choose a data source,
   * and a moment later it is gone. It LOOKED like it worked, because Mantine's
   * `Select` keeps its own display value — the field still read "Orders" while the
   * definition had `cube: ''` and every cube-dependent section stayed locked with
   * no explanation. Combined with the completeness gate below (an incomplete card
   * is never persisted, so the subscription never carries the edit back), there was
   * no order in which a new card could ever be finished.
   *
   * So the re-seed is now conditional:
   *
   *   - **a DIFFERENT report** (`id` changed) always re-seeds — that is a genuine
   *     navigation, and the previous report's working copy is irrelevant to it;
   *   - **not editing** always re-seeds — a viewer SHOULD track the readmodel live;
   *   - **editing the same report** never re-seeds — the person typing wins.
   *
   * Keyed on the report's `id` rather than object identity, because identity churn
   * is exactly the signal that turned out to be meaningless here.
   */
  const seededLayoutIdRef = useRef<string | undefined>( builderLayout?.id );
  useEffect(() => {
    const isDifferentReport = builderLayout?.id !== seededLayoutIdRef.current;
    if ( !isDifferentReport && isEditModeOn ) return;
    seededLayoutIdRef.current = builderLayout?.id;
    setLayoutState( builderLayout );
  }, [ builderLayout, isEditModeOn ]);
  useEffect(()=> {
    const deviceTypes = getDeviceWidgetTypes( availableWidgets, layout.device );
    setDeviceAvailableWidgets( deviceTypes );
  }, [ availableWidgets, layout.device ]);
  //handlers
  /**
   * Caller-supplied validators only — there is NO default one any more
   * (TV1-2291 · S10 / SDD §2, concern C1).
   *
   * `noOverlapValidator` used to sit here, and it is now deleted. It existed
   * because the engine REFUSED the situation instead of resolving it: two widgets
   * touching rejected the whole save, so a drop one pixel into a neighbour was a
   * dead end — an error toast and nothing persisted. The editor now resolves it by
   * construction (`placement.compact`, wired into every drag and resize in
   * `WidgetLayoutEditor`), which makes the validator not merely redundant but
   * WRONG: it would reject layouts the resolver legitimately produces.
   *
   * ⚠️ The two changes are one slice on purpose. Retiring the validator WITHOUT
   * compaction wired leaves a window where overlap is silently permitted and
   * persisted — a worse state than either end. If you are reverting one of them,
   * revert both.
   *
   * Memoised so `handleLayoutChange`'s identity does not churn on every render.
   */
  const activeValidators = useMemo<ValidatorFn[]>(() => validators ?? [], [validators]);
  const handleLayoutChange = useCallback(( update: WidgetLayout ) => {
      // TV1-1931 — materialize the auto-grid ONCE, here, on the write path.
      // Validators must see concrete rects (a positionless widget makes every
      // overlap comparison NaN → `false` → overlap silently undetected), and
      // persisting the resolved layout means the first human edit gives every
      // widget concrete coordinates, so no mixed state ever reaches storage.
      //
      // This is the only place resolution is written back. The Editor/Viewer
      // resolve in a `useMemo` for RENDER only — resolving into `layoutState`
      // outside this write path would retrigger the Cube fetch effect below,
      // which keys on `[layoutState, cubeApi]`.
      const resolved = resolveLayout( update );
      const newErrors = activeValidators.flatMap(( v ) => v( resolved, t ));
      if ( newErrors.length ) {
        setErrors( newErrors );
        onValidationError?.( newErrors );
        return;
      }
      // PREFACTOR-grammar — cross back to the domain grammar HERE, on the write
      // path, so the caller receives a body `AddUiWidgetLayout` will actually
      // accept. Before this, `Reports.tsx` shipped the builder form straight into
      // the command and the aggregate rejected it with ~18 ajv errors, which
      // `catch ( err ) { console.log( err ) }` then swallowed.
      const domainBody = toDomainLayout( resolved );

      // TV1-1982 (ADR-061) — validate the domain body against the SAME schema the
      // server enforces (`UiWidgetsLayoutSchema` = `AddUiWidgetLayoutCommandSchema`),
      // before dispatch. Same ajv engine + same schema as the aggregate, so this
      // rejects only what the server would ALSO reject — it never blocks a valid save,
      // it just turns a swallowed 400 into a visible, local, fail-fast error.
      const schemaValidation = validateDomainLayout( domainBody );

      /*
       * ⚠️ THE EDITOR'S STATE UPDATES EVEN WHEN THE LAYOUT CANNOT BE SAVED YET, AND
       * THIS SEPARATION IS THE WHOLE FUNCTION (2026-08-17).
       *
       * `onChange` is the PERSISTENCE seam — `Reports.tsx` turns it straight into a
       * `ChangeUiWidgetLayout` command. `setLayoutState` is the EDITOR's working
       * copy. Those are different questions, and conflating them made the builder
       * completely unusable:
       *
       *   Adding a card mints it blank, and `UiWidgetSchema` requires a name, a data
       *   source and at least one measure. So a new card is invalid BY CONSTRUCTION.
       *   This function used to `return` before `setLayoutState` on a schema failure,
       *   which meant the editor state never moved — and since the user can only set
       *   one field at a time, EVERY intermediate step was rejected. Choose a cube:
       *   rejected, discarded. Type a name: rejected, discarded. There was no order
       *   in which a card could ever be configured, because the only accepted state
       *   was one you could not reach incrementally.
       *
       *   It looked like it worked, which is why it shipped: Mantine's `Select` keeps
       *   its own display value, so the field showed "Orders" while the definition
       *   still had `cube: ''` — and the sections that unlock on a cube stayed
       *   disabled with no explanation.
       *
       * So: the working copy ALWAYS takes the edit; only the SAVE is gated. An
       * incomplete card lives in the editor, renders its "configure me" placeholder,
       * and is simply not persisted until it is complete.
       *
       * ⚠️ A caller-supplied validator still short-circuits above — those are
       * explicit rules from the host, not a completeness check, and refusing them
       * outright is the contract they were added under.
       */
      // Reached only when the caller's validators passed (they `return` above), so
      // there is nothing left to display either way — an incomplete card is not an
      // error, it is an unfinished one.
      setErrors([]);
      setLayoutState( resolved );

      if ( !schemaValidation.valid ) {
        /*
         * NO TOAST. This is reached on ordinary in-progress typing (the inspector
         * commits on a 500ms idle), so a toast here fires repeatedly while someone
         * fills in a brand-new card — which is precisely what "cant add widgets it
         * fires errors" was describing.
         *
         * The state is already reported where it belongs and without interrupting:
         * the card draws "Configurá esta tarjeta", and the inspector lists the
         * missing fields inline, per section, next to the inputs that fix them.
         * `onValidationError` still fires so a host that wants to know, knows.
         */
        onValidationError?.([{
          code: 'widget_schema_incomplete',
          name: t( 'widget_schema_invalid_title', 'Invalid widget definition' ),
          message: schemaValidation.errors.slice( 0, 3 ).join( '; ' )
        }]);
        return;
      }

      onChange( domainBody as PersistedWidgetLayout );
    },
    [ activeValidators, onChange, onValidationError, t ]);
  return (
    <div className={classes.centerContent}>
        {editMode ? <WidgetLayoutEditor layout={layoutState} widgetDataMap={widgetDataMap} onChange={handleLayoutChange} availableWidgets={deviceAvailableWidgets} scale={scale} cubeApi={cubeApi} /> : <WidgetLayoutViewer layout={layoutState} widgetDataMap={widgetDataMap} scale={scale} surface={surface} />}
    </div>
  );
};

export default WidgetLayoutComponent;